import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Upload, Loader2, Camera } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { usersApi, postsApi } from '@/lib/api'
import { useAuth } from '@/hooks/useAuth'
import { getInitials } from '@/lib/utils'
import { useToast } from '@/hooks/useToast'

interface EditProfileModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ProfileFormData {
  display_name: string
  bio: string
  avatar_url?: string
  cover_url?: string
  location: string
  website: string
}

export function EditProfileModal({ open, onOpenChange }: EditProfileModalProps) {
  const { user, refreshUser } = useAuth()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [formData, setFormData] = useState<ProfileFormData>({
    display_name: '',
    bio: '',
    avatar_url: '',
    cover_url: '',
    location: '',
    website: '',
  })

  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [coverFile, setCoverFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string>('')
  const [coverPreview, setCoverPreview] = useState<string>('')
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false)
  const [isUploadingCover, setIsUploadingCover] = useState(false)

  // Load user data when modal opens
  useEffect(() => {
    if (open && user) {
      setFormData({
        display_name: user.displayName || '',
        bio: user.bio || '',
        avatar_url: user.avatarUrl || '',
        cover_url: user.coverUrl || '',
        location: user.location || '',
        website: user.website || '',
      })
      setAvatarPreview(user.avatarUrl || '')
      setCoverPreview(user.coverUrl || '')
    }
  }, [open, user])

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: Partial<ProfileFormData>) => {
      const response = await usersApi.updateProfile(data)
      return response.data
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      queryClient.invalidateQueries({ queryKey: ['user'] })
      await refreshUser() // Refresh auth state to update user object
      toast({ title: 'Success', description: 'Profile updated successfully' })
      onOpenChange(false)
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update profile',
        variant: 'destructive',
      })
    },
  })

  // Upload file to media service
  const uploadFile = async (file: File): Promise<string> => {
    const formData = new FormData()
    formData.append('file', file)

    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL || ''}/api/v1/uploads`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || ''}`,
        },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData?.error?.message || 'Upload failed')
      }

      const data = await response.json()
      // Response format: { success: true, data: { url: "...", ... } }
      return data.data?.url || data.url
    } catch (error) {
      console.error('Upload error:', error)
      throw error
    }
  }

  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Avatar must be less than 5MB',
          variant: 'destructive',
        })
        return
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Avatar must be an image',
          variant: 'destructive',
        })
        return
      }

      setAvatarFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setAvatarPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast({
          title: 'Error',
          description: 'Cover image must be less than 10MB',
          variant: 'destructive',
        })
        return
      }

      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Error',
          description: 'Cover image must be an image',
          variant: 'destructive',
        })
        return
      }

      setCoverFile(file)
      const reader = new FileReader()
      reader.onloadend = () => {
        setCoverPreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate display name
    if (formData.display_name.trim().length === 0) {
      toast({
        title: 'Error',
        description: 'Display name is required',
        variant: 'destructive',
      })
      return
    }

    if (formData.display_name.length > 50) {
      toast({
        title: 'Error',
        description: 'Display name must be 50 characters or less',
        variant: 'destructive',
      })
      return
    }

    if (formData.bio.length > 160) {
      toast({
        title: 'Error',
        description: 'Bio must be 160 characters or less',
        variant: 'destructive',
      })
      return
    }

    if (formData.website && !isValidUrl(formData.website)) {
      toast({
        title: 'Error',
        description: 'Please enter a valid website URL',
        variant: 'destructive',
      })
      return
    }

    try {
      const updateData: Partial<ProfileFormData> = {
        display_name: formData.display_name,
        bio: formData.bio,
        location: formData.location,
        website: formData.website,
      }

      // Upload avatar if changed
      if (avatarFile) {
        setIsUploadingAvatar(true)
        try {
          const url = await uploadFile(avatarFile)
          updateData.avatar_url = url
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to upload avatar',
            variant: 'destructive',
          })
          setIsUploadingAvatar(false)
          return
        }
        setIsUploadingAvatar(false)
      }

      // Upload cover if changed
      if (coverFile) {
        setIsUploadingCover(true)
        try {
          const url = await uploadFile(coverFile)
          updateData.cover_url = url
        } catch (error) {
          toast({
            title: 'Error',
            description: 'Failed to upload cover image',
            variant: 'destructive',
          })
          setIsUploadingCover(false)
          return
        }
        setIsUploadingCover(false)
      }

      // Update profile
      await updateProfileMutation.mutateAsync(updateData)
    } catch (error) {
      console.error('Profile update error:', error)
    }
  }

  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const bioLength = formData.bio.length
  const isLoading = updateProfileMutation.isPending || isUploadingAvatar || isUploadingCover

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Profile</DialogTitle>
          <DialogDescription>
            Update your profile information and appearance
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Cover Image */}
          <div className="space-y-2">
            <Label>Cover Image</Label>
            <div className="relative h-32 sm:h-48 bg-gradient-to-r from-primary/30 to-accent/30 rounded-lg overflow-hidden group">
              {coverPreview && (
                <img
                  src={coverPreview}
                  alt="Cover preview"
                  className="h-full w-full object-cover"
                />
              )}
              <label
                htmlFor="cover-upload"
                className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              >
                <div className="text-white text-center">
                  <Camera className="h-8 w-8 mx-auto mb-2" />
                  <span className="text-sm">Change Cover</span>
                </div>
              </label>
              <input
                id="cover-upload"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleCoverChange}
                disabled={isLoading}
              />
            </div>
            {isUploadingCover && (
              <p className="text-xs text-muted-foreground flex items-center gap-2">
                <Loader2 className="h-3 w-3 animate-spin" />
                Uploading cover image...
              </p>
            )}
          </div>

          {/* Avatar */}
          <div className="space-y-2">
            <Label>Avatar</Label>
            <div className="flex items-center gap-4">
              <div className="relative group">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={avatarPreview} />
                  <AvatarFallback className="text-2xl">
                    {getInitials(formData.display_name)}
                  </AvatarFallback>
                </Avatar>
                <label
                  htmlFor="avatar-upload"
                  className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <Camera className="h-5 w-5 text-white" />
                </label>
                <input
                  id="avatar-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleAvatarChange}
                  disabled={isLoading}
                />
              </div>
              <div className="flex-1 text-sm text-muted-foreground">
                <p>Recommended: Square image, at least 400x400px</p>
                <p className="text-xs">Maximum size: 5MB</p>
                {isUploadingAvatar && (
                  <p className="text-xs flex items-center gap-2 mt-1">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Uploading avatar...
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Display Name */}
          <div className="space-y-2">
            <Label htmlFor="display_name">
              Display Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="display_name"
              value={formData.display_name}
              onChange={(e) =>
                setFormData({ ...formData, display_name: e.target.value })
              }
              maxLength={50}
              required
              disabled={isLoading}
            />
            <p className="text-xs text-muted-foreground">
              {formData.display_name.length}/50 characters
            </p>
          </div>

          {/* Bio */}
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={formData.bio}
              onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
              maxLength={160}
              rows={3}
              placeholder="Tell people a little about yourself..."
              disabled={isLoading}
            />
            <p
              className={`text-xs ${
                bioLength > 160 ? 'text-destructive' : 'text-muted-foreground'
              }`}
            >
              {bioLength}/160 characters
            </p>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              maxLength={100}
              placeholder="Where are you based?"
              disabled={isLoading}
            />
          </div>

          {/* Website */}
          <div className="space-y-2">
            <Label htmlFor="website">Website</Label>
            <Input
              id="website"
              type="url"
              value={formData.website}
              onChange={(e) =>
                setFormData({ ...formData, website: e.target.value })
              }
              placeholder="https://example.com"
              disabled={isLoading}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
